// Global error handling for Turbo and Stimulus applications

// Handle unhandled promise rejections (including "Load failed" errors)
window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;

    // Check if it's a "Load failed" error
    if (error instanceof TypeError && error.message.includes('Load failed')) {
        console.warn('Resource load failed:', error);

        // Try to extract more context
        const stack = error.stack;
        if (stack) {
            console.warn('Error stack:', stack);
        }

        // Optionally show user-friendly message
        window.showErrorMessage?.('Failed to load resource. Please refresh the page.');

        // Prevent the error from being logged to console (optional)
        event.preventDefault();
        return;
    }

    // Handle other unhandled rejections
    console.error('Unhandled promise rejection:', error);
});

// Handle general JavaScript errors
window.addEventListener('error', (event) => {
    const {error, filename, lineno, colno} = event;

    // Check if it's a resource loading error
    if (event.target !== window && event.target.tagName) {
        const tag = event.target.tagName.toLowerCase();
        const src = event.target.src || event.target.href;

        console.warn(`Failed to load ${tag}:`, src);

        // Handle specific resource types
        if (tag === 'script') {
            console.warn('JavaScript file failed to load, this may cause functionality issues');
        } else if (tag === 'link') {
            console.warn('CSS file failed to load, this may affect styling');
        } else if (tag === 'img') {
            // Set a fallback image or hide broken images
            event.target.style.display = 'none';
        }

        return;
    }

    // Handle script errors
    if (error) {
        console.error('JavaScript error:', {
            message: error.message,
            filename,
            line: lineno,
            column: colno,
            stack: error.stack,
        });
    }
});

// Turbo-specific error handling
document.addEventListener('turbo:fetch-request-error', (event) => {
    const {request, error} = event.detail;
    console.error('Turbo fetch error:', {
        url: request.url,
        method: request.method,
        error: error.message,
    });

    // Show user-friendly error message
    window.showErrorMessage?.('Network request failed. Please check your connection.');
});

// Handle Turbo frame load errors
document.addEventListener('turbo:frame-missing', (event) => {
    const {
        target,
        detail: {fetchResponse},
    } = event;
    console.warn('Turbo frame missing:', {
        frameId: target.id,
        response: fetchResponse,
    });
});

// Handle Turbo form submission errors
document.addEventListener('turbo:submit-end', (event) => {
    const {formSubmission} = event.detail;

    if (!formSubmission.result.succeeded) {
        const error = formSubmission.result.error;
        if (error) {
            console.error('Form submission failed:', error);

            // Check if it's a network-related error
            if (error.message?.includes('Load failed') || error.message?.includes('fetch')) {
                window.showErrorMessage?.('Form submission failed. Please try again.');
            }
        }
    }
});

// Stimulus controller error handling
document.addEventListener('stimulus:connect-error', (event) => {
    const {controller, error} = event.detail;
    console.error(`Stimulus controller "${controller.identifier}" connection error:`, error);
});

// Helper function to retry failed requests
window.retryFailedRequest = async (url, options = {}, maxRetries = 3) => {
    let lastError;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            const response = await fetch(url, options);
            if (response.ok) {
                return response;
            }
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        } catch (error) {
            lastError = error;
            console.warn(`Request attempt ${attempt} failed:`, error.message);

            if (attempt < maxRetries) {
                // Exponential backoff: wait 1s, 2s, 4s, etc.
                const delay = Math.pow(2, attempt - 1) * 1000;
                await new Promise((resolve) => setTimeout(resolve, delay));
            }
        }
    }

    throw lastError;
};

// Network connectivity monitoring
let isOnline = navigator.onLine;

window.addEventListener('online', () => {
    if (!isOnline) {
        isOnline = true;
        window.showSuccessMessage?.('Connection restored');
        console.warn('Network connection restored');
    }
});

window.addEventListener('offline', () => {
    isOnline = false;
    window.showErrorMessage?.('Connection lost. Some features may not work.');
    console.warn('Network connection lost');
});

// Export utilities for use in other modules
window.errorHandlers = {
    retryFailedRequest: window.retryFailedRequest,
    isOnline: () => isOnline,
};
